/**
 * HeartMonitorPanel — pair/unpair UI + live HRV readout in one card.
 *
 * Mounted inside the Beat Settings panel below Coherence. Hidden entirely
 * when Web Bluetooth isn't supported (Safari/Firefox) so iOS users see a
 * clean UI rather than a dead button.
 *
 * States the panel cycles through:
 *   idle         → "Connect heart monitor" button + $59-lifetime trial teaser
 *   connecting   → spinner + "Selecting device..."
 *   connected    → device name + live BPM + RMSSD + Rhythm Score + disconnect
 *   disconnected → "Lost connection" + reconnect button
 *   error        → error message + retry button
 *
 * The "sync heartbeat to my heart" toggle only appears once connected AND
 * beatType === 'heartbeat'. Wiring into the audio engine is the caller's
 * responsibility — this component is pure presentation + pairing.
 *
 * Trial teaser: during the beta phase every user gets the feature free.
 * The teaser ("Beta · becomes $59 lifetime with a 22-day free trial")
 * sets price expectations so the inevitable paywall doesn't feel abrupt.
 */
import React, { useState } from 'react';
import { HeartPulse, Loader2, Bluetooth, X, RefreshCw, Info, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';

const formatMs = (v) => (v == null ? '—' : `${Math.round(v)} ms`);
const formatScore = (v) => (v == null ? '—' : Math.round(v));

// Metric explanations — surfaced as tap-to-reveal popovers next to each
// number so users (and friends) can self-serve "what does this mean?"
// without needing to ask. Copy lifted from the agent's longer-form
// explanation in chat — pared down to the bare essentials that fit in
// a card without scrolling.
const METRIC_EXPLAINERS = {
  bpm: {
    title: 'BPM — Beats per minute',
    body: 'How many times your heart beats per minute. Resting adults: usually 60-100. Athletes: 40-60. Elevated during stress, caffeine, or exertion.',
    benchmarks: null,
  },
  hrv: {
    title: 'HRV (RMSSD) — Heart rate variability',
    body: 'The average difference between consecutive heartbeats, in milliseconds. Driven by your vagus nerve (rest-and-digest). Higher = better recovery and resilience.',
    benchmarks: [
      ['< 20 ms', 'Stressed / tired / recovering'],
      ['20-40 ms', 'Average untrained adult'],
      ['40-70 ms', 'Healthy & well-rested'],
      ['70+ ms', 'Athlete / deep meditation'],
    ],
  },
  rhythm: {
    title: 'Rhythm — Coherence (0-100)',
    body: 'How stable your HRV pattern is across this session. Climbs as your breath and heart lock together. Watch it rise from low → 80+ during a Coherence session — that\'s the training effect.',
    benchmarks: [
      ['70-100', 'Steady, coherent rhythm'],
      ['40-70', 'Drifting / transitioning'],
      ['0-40', 'Erratic (or loose strap)'],
    ],
  },
};

const MetricCell = ({ testId, value, label, explainerKey, openKey, setOpenKey }) => {
  const isOpen = openKey === explainerKey;
  return (
    <div data-testid={testId}>
      <div className="text-white text-2xl font-semibold tabular-nums">
        {value}
      </div>
      <button
        type="button"
        onClick={() => setOpenKey(isOpen ? null : explainerKey)}
        data-testid={`hrv-explainer-toggle-${explainerKey}`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/40 hover:text-white/70 transition-colors"
        aria-expanded={isOpen}
      >
        {label}
        <HelpCircle className="w-3 h-3 opacity-60" />
      </button>
    </div>
  );
};

const MetricExplainer = ({ explainerKey, onClose }) => {
  const e = METRIC_EXPLAINERS[explainerKey];
  if (!e) return null;
  return (
    <div
      className="rounded-lg border border-white/8 bg-white/[0.03] p-3 text-[11px] leading-relaxed space-y-2"
      data-testid={`hrv-explainer-${explainerKey}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-white/85 font-medium text-xs">{e.title}</div>
        <button
          type="button"
          onClick={onClose}
          data-testid={`hrv-explainer-close-${explainerKey}`}
          className="text-white/30 hover:text-white/70 -mr-1 -mt-0.5"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="text-white/55">{e.body}</p>
      {e.benchmarks && (
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 pt-1 border-t border-white/5">
          {e.benchmarks.map(([range, meaning]) => (
            <React.Fragment key={range}>
              <span className="text-cyan-200 tabular-nums">{range}</span>
              <span className="text-white/45">{meaning}</span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// Map a raw Web Bluetooth error message + user agent to a friendly,
// actionable diagnosis. The browser's own errors are technically accurate
// but useless to non-engineers ("Web Bluetooth API globally disabled."
// tells you nothing about WHAT to do). This function returns the same
// "what + how to fix" pair you'd get from a support engineer.
const diagnoseBluetoothError = (rawError) => {
  const msg = String(rawError || '').toLowerCase();
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isBrave = ua.includes('Brave') ||
    (typeof navigator !== 'undefined' && navigator.brave?.isBrave);
  const isMac = /mac|darwin/i.test(ua);
  const isLinux = /linux/i.test(ua) && !/android/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isWindows = /windows/i.test(ua);
  const isIOS = /iphone|ipad|ipod/i.test(ua) ||
    (isMac && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);

  // Permissions-Policy / Feature-Policy block — fires when the page is
  // embedded in an iframe (like Emergent's preview wrapper) whose parent
  // doesn't include `allow="bluetooth"`. The app itself can't override
  // this; the parent frame has to delegate the permission. Easiest user
  // workaround is "open in new tab" — the standalone window has full
  // Bluetooth access.
  if (msg.includes('permissions policy') || msg.includes('feature policy') ||
      msg.includes('disallowed by permissions') ||
      msg.includes('not allowed by permissions') ||
      msg.includes('not allowed in this document')) {
    return {
      title: 'Bluetooth blocked by this page wrapper',
      fix: 'You\'re viewing this inside an iframe (preview/embed) that doesn\'t allow Bluetooth. Open the app in its own tab and try again.',
      action: { label: 'Open in new tab', kind: 'newtab' },
    };
  }

  // "Globally disabled" — the highest-signal Chrome/Brave error. Almost
  // always means a flag or shield is off at the browser level.
  if (msg.includes('globally disabled')) {
    if (isBrave) {
      return {
        title: 'Brave has Bluetooth turned off',
        fix: 'Open brave://settings/privacy → Web3 / Bluetooth → enable. Or try Chrome / Edge.',
      };
    }
    if (isLinux) {
      return {
        title: 'Web Bluetooth disabled in Chrome',
        fix: 'Visit chrome://flags/#enable-experimental-web-platform-features, enable it, restart Chrome.',
      };
    }
    return {
      title: 'Browser blocked Bluetooth',
      fix: 'Likely an enterprise/work device policy. Try a personal device, or use Chrome / Edge on desktop.',
    };
  }

  // OS-level permission denied (mainly macOS Sequoia and later)
  if (msg.includes('permission') && msg.includes('denied')) {
    if (isMac) {
      return {
        title: 'macOS blocked Bluetooth access',
        fix: 'System Settings → Privacy & Security → Bluetooth → enable for this browser. Then restart the browser.',
      };
    }
    if (isAndroid) {
      return {
        title: 'Android needs Location permission',
        fix: 'Settings → Apps → Chrome → Permissions → Location: allow. (Required for BLE scanning.)',
      };
    }
    return {
      title: 'OS denied Bluetooth access',
      fix: 'Check your system Bluetooth privacy settings and grant access to this browser.',
    };
  }

  // User cancelled the device chooser — common, not really an error.
  if (msg.includes('user cancelled') || msg.includes('user canceled') || msg.includes('chooser')) {
    return {
      title: 'No device picked',
      fix: 'Wake your strap (clip it on / tap it) so it advertises, then try again.',
    };
  }

  // Bluetooth radio off at the OS level
  if (msg.includes('not turned on') || msg.includes('powered off') || msg.includes('not available')) {
    return {
      title: 'Bluetooth is off',
      fix: 'Turn on Bluetooth in your OS settings, wake the strap, then retry.',
    };
  }

  // No supported browser at all
  if (msg.includes('not supported') || isIOS) {
    return {
      title: 'Browser doesn\'t support Web Bluetooth',
      fix: 'Use Chrome, Edge, or Brave (with shields off) on desktop or Android. iOS Safari is not supported.',
    };
  }

  // Generic fallback — keep the original message visible for debugging
  return {
    title: rawError || 'Could not connect',
    fix: 'Wake your strap, ensure Bluetooth is on, and try again. ' +
      (isWindows ? 'On Windows: pair the strap in Settings first if needed.' : ''),
  };
};

// Hardware compatibility matrix. The pairing flow reads the standard BLE
// Heart Rate Service (UUID 0x180D) — which most chest straps and some arm
// bands broadcast publicly. WHOOP, Apple Watch, Fitbit, Oura and most
// consumer smartwatches don't — they keep HR data inside their own app
// or cloud. Surfacing this list up-front saves frustrated WHOOP/Apple
// Watch users 10 minutes of pairing failure and a support email.
const COMPATIBILITY = {
  works: [
    'Polar H10, H9 (chest strap — gold standard)',
    'Polar Verity Sense / OH1 (arm band)',
    'Wahoo TICKR / TICKR X / TICKR Fit',
    'Garmin HRM-Pro / HRM-Dual (broadcast mode on)',
    'Coospo H9 / H6 (chest strap)',
    'Most straps labeled "Bluetooth Smart" or "Bluetooth LE"',
  ],
  doesntWork: [
    'WHOOP (proprietary — no public BLE broadcast)',
    'Apple Watch (HealthKit-only, no BLE HR)',
    'Fitbit (closed ecosystem)',
    'Oura ring (cloud-only, not real-time)',
    'Most smartwatches without "HR broadcast" toggle',
  ],
};

export const HeartMonitorPanel = ({
  monitor,          // the full useHeartMonitor() return value
  beatType,         // to gate "sync heartbeat" toggle
  syncHeartbeatToUser,
  onToggleSyncHeartbeatToUser,
  onUpgradeClick,   // opens ProUpgradeModal — optional, no-op if unset
}) => {
  const [compatOpen, setCompatOpen] = useState(false);
  // Which metric explainer (if any) is currently expanded. null = none.
  // Single-slot so opening one auto-closes the others — keeps the card
  // tight and the focus on one concept at a time.
  const [explainerOpen, setExplainerOpen] = useState(null);
  // Unsupported browser → render nothing. Saves clutter on iOS/Firefox.
  if (!monitor.isSupported) return null;

  const { status, deviceName, bpm, rmssd, rhythmScore, sampleCount, error,
          connect, disconnect } = monitor;

  // Calibrating heuristic: we need ~30 intervals (~30s at 60bpm) before
  // RMSSD stabilises. Below that threshold, show soft "Calibrating..."
  // UI even though we technically have numbers — keeps the user from
  // trusting wildly noisy values in the first seconds.
  const calibrating = status === 'connected' && sampleCount < 30;

  return (
    <div
      className="rounded-lg border border-rose-400/20 bg-rose-500/[0.04] p-4 space-y-3"
      data-testid="heart-monitor-panel"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-4 h-4 text-rose-400" />
          <h4 className="text-sm font-medium text-white">Live heart monitor</h4>
        </div>
        <span
          className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200"
          data-testid="hrv-beta-badge"
          title="Free during beta · becomes $59 lifetime with 22-day trial"
        >
          Beta
        </span>
      </div>

      {/* IDLE — pair prompt + pricing teaser */}
      {status === 'idle' && (
        <div className="space-y-3">
          <p className="text-xs text-white/60 leading-relaxed">
            Pair a Bluetooth heart-rate strap (Polar H10, Wahoo Tickr,
            Coospo H9) for live HRV during sessions. Your heartbeat drives
            the audio — literally.
          </p>
          <Button
            type="button"
            onClick={connect}
            data-testid="hrv-connect-button"
            variant="secondary"
            className="w-full gap-2"
          >
            <Bluetooth className="w-4 h-4" />
            Connect heart monitor
          </Button>

          {/* Compatibility disclosure — collapsed by default so the idle
              card stays clean for first-time visitors, but one tap reveals
              the full works/doesn't-work matrix. Critical for WHOOP / Apple
              Watch / Fitbit users who'd otherwise try to pair and fail
              silently (Web Bluetooth chooser only shows devices broadcasting
              the standard 0x180D HR service, so non-compatible hardware
              just doesn't appear). */}
          <button
            type="button"
            onClick={() => setCompatOpen((v) => !v)}
            data-testid="hrv-compat-toggle"
            className="w-full flex items-center justify-center gap-1.5 text-[11px] text-white/45 hover:text-white/75 transition-colors py-1"
          >
            <Info className="w-3 h-3" />
            {compatOpen ? 'Hide' : 'Which devices work?'}
          </button>

          {compatOpen && (
            <div
              className="rounded-lg border border-white/8 bg-white/[0.02] p-3 space-y-3 text-[11px] leading-relaxed"
              data-testid="hrv-compat-matrix"
            >
              <div>
                <div className="text-emerald-300 font-medium mb-1 text-xs">
                  ✓ Works (live R-peak data)
                </div>
                <ul className="space-y-0.5 text-white/65 pl-3">
                  {COMPATIBILITY.works.map((d) => (
                    <li key={d} className="list-disc list-outside">{d}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-rose-300 font-medium mb-1 text-xs">
                  ✗ Won't pair (no public BLE broadcast)
                </div>
                <ul className="space-y-0.5 text-white/55 pl-3">
                  {COMPATIBILITY.doesntWork.map((d) => (
                    <li key={d} className="list-disc list-outside">{d}</li>
                  ))}
                </ul>
              </div>
              <p className="text-white/35 text-[10px] pt-1 border-t border-white/5">
                We read the standard Bluetooth Heart Rate Profile (service
                <code className="px-1 mx-0.5 rounded bg-white/5 text-white/55">0x180D</code>).
                Most chest straps broadcast it; most smartwatches keep
                HR data inside their own app.
              </p>
            </div>
          )}
          <p className="text-[11px] text-white/35 text-center">
            Free during beta ·{' '}
            <button
              type="button"
              onClick={onUpgradeClick}
              data-testid="hrv-upgrade-link"
              className="underline underline-offset-2 decoration-white/20 hover:text-white/70 hover:decoration-white/40 transition-colors"
            >
              $59 lifetime with 22-day trial at launch
            </button>
          </p>
        </div>
      )}

      {/* CONNECTING */}
      {status === 'connecting' && (
        <div className="flex items-center justify-center gap-2 py-4 text-white/60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Selecting device…</span>
        </div>
      )}

      {/* CONNECTED — live readout */}
      {status === 'connected' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span
              className="text-xs text-emerald-300 inline-flex items-center gap-1"
              data-testid="hrv-device-name"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {deviceName}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={disconnect}
              data-testid="hrv-disconnect-button"
              className="h-6 px-2 text-xs text-white/50 hover:text-white gap-1"
            >
              <X className="w-3 h-3" />
              Disconnect
            </Button>
          </div>

          {/* Three-metric grid. tabular-nums keeps numbers from jumping
              as they update every 5s. */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <MetricCell
              testId="hrv-bpm-cell"
              value={bpm ?? '—'}
              label="BPM"
              explainerKey="bpm"
              openKey={explainerOpen}
              setOpenKey={setExplainerOpen}
            />
            <MetricCell
              testId="hrv-rmssd-cell"
              value={calibrating ? '…' : formatMs(rmssd)}
              label="HRV (RMSSD)"
              explainerKey="hrv"
              openKey={explainerOpen}
              setOpenKey={setExplainerOpen}
            />
            <MetricCell
              testId="hrv-rhythm-cell"
              value={calibrating ? '…' : formatScore(rhythmScore)}
              label="Rhythm"
              explainerKey="rhythm"
              openKey={explainerOpen}
              setOpenKey={setExplainerOpen}
            />
          </div>

          {/* Inline explainer panel — appears directly under the metrics
              grid when any of the three "?" buttons is tapped. Single-
              slot, so opening one closes the others. */}
          {explainerOpen && (
            <MetricExplainer
              explainerKey={explainerOpen}
              onClose={() => setExplainerOpen(null)}
            />
          )}

          {calibrating && (
            <p className="text-[11px] text-white/40 text-center">
              Calibrating… wait 30 seconds for reliable HRV.
            </p>
          )}

          {/* Sync heartbeat audio to the user's real R-peaks. Only makes
              sense when beatType is 'heartbeat' — the toggle is otherwise
              hidden to avoid confusion. */}
          {beatType === 'heartbeat' && (
            <div className="flex items-center justify-between pt-2 border-t border-white/5">
              <Label
                htmlFor="hrv-sync-toggle"
                className="text-xs text-white/70 cursor-pointer"
              >
                Sync heartbeat to my heart
              </Label>
              <Switch
                id="hrv-sync-toggle"
                checked={syncHeartbeatToUser}
                onCheckedChange={onToggleSyncHeartbeatToUser}
                data-testid="hrv-sync-toggle"
              />
            </div>
          )}
        </div>
      )}

      {/* DISCONNECTED — soft reconnect */}
      {status === 'disconnected' && (
        <div className="space-y-2">
          <p className="text-xs text-amber-300">Lost connection. Strap awake?</p>
          <Button
            type="button"
            size="sm"
            onClick={connect}
            data-testid="hrv-reconnect-button"
            variant="secondary"
            className="w-full gap-2"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Reconnect
          </Button>
        </div>
      )}

      {/* ERROR */}
      {status === 'error' && (() => {
        const { title, fix, action } = diagnoseBluetoothError(error);
        const handleAction = () => {
          if (action?.kind === 'newtab' && typeof window !== 'undefined') {
            window.open(window.location.href, '_blank', 'noopener,noreferrer');
          }
        };
        return (
          <div className="space-y-2" data-testid="hrv-error-block">
            <div
              className="rounded-md border border-rose-400/20 bg-rose-500/[0.06] px-3 py-2 space-y-1"
              data-testid="hrv-error-message"
            >
              <p className="text-xs text-rose-200 font-medium leading-snug">
                {title}
              </p>
              <p className="text-[11px] text-white/55 leading-relaxed" data-testid="hrv-error-fix">
                {fix}
              </p>
            </div>
            <div className="flex gap-2">
              {action?.kind === 'newtab' && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAction}
                  data-testid="hrv-error-action"
                  className="flex-1 gap-2"
                >
                  {action.label}
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                onClick={connect}
                data-testid="hrv-error-retry"
                variant="secondary"
                className="flex-1 gap-2"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Try again
              </Button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default HeartMonitorPanel;
